import React from 'react';
import Modal from 'react-modal';
import CloseIcon from '../ui/icons/CloseIcon';
import { useResponsive } from '../../hooks/useResponsive';

/**
 * GenericModal
 *
 * Props:
 * - isOpen: boolean
 * - toggle: function (close handler)
 * - title: string (optional, ignored if titleSlot is provided)
 * - titleSlot: ReactNode (optional, custom title area)
 * - description: string (optional)
 * - onSubmit: function (main action)
 * - onClose: function (cancel action)
 * - isSubmitting: boolean
 * - submitDisabled: boolean (disable Confirmar button)
 * - children: ReactNode (content)
 * - showLogo: boolean (show default logo)
 * - closable: boolean (show X button)
 * - contentPadding: string (CSS padding for content area)
 * - width: string (CSS width, e.g. '520px')
 * - destructive: boolean (if true, submit button will be red/destructive style)
 * - mobileBottomSheet: boolean (default true) - Use bottom sheet on mobile
 * - shouldCloseOnOverlayClick: boolean (default true) - passed to react-modal
 * - shouldCloseOnEsc: boolean (default true) - passed to react-modal
 * - overlayZIndex: number (default 10000) - stack nested modals above the parent
 */

/** Spread onto GenericModal for mandatory flows (no X, overlay, or Esc close). */
export const NON_DISMISSIBLE_MODAL_PROPS = {
  closable: false,
  shouldCloseOnOverlayClick: false,
  shouldCloseOnEsc: false,
};

const GenericModal = ({
  isOpen,
  toggle,
  title,
  titleSlot,
  description,
  onSubmit,
  onClose,
  isSubmitting,
  submitDisabled = false,
  children,
  showLogo = true,
  closable = false,
  contentPadding = '0 2.5rem 2rem 2.5rem',
  width = '520px',
  maxHeight = '90vh',
  showSubmitButton = false,
  showCloseButton = false,
  submitButtonText = 'Confirmar',
  closeButtonText = 'Cancelar',
  destructive = false,
  mobileBottomSheet = true,
  shouldCloseOnOverlayClick = true,
  shouldCloseOnEsc = true,
  overlayZIndex = 10000,
}) => {
  const { isMobile, isDesktop } = useResponsive();
  const hasFooter = showSubmitButton || showCloseButton;
  const useMobileStyle = isMobile && mobileBottomSheet;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={toggle}
      contentLabel={title || 'Modal'}
      ariaHideApp={false}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      shouldCloseOnEsc={shouldCloseOnEsc}
      style={{
        overlay: {
          backgroundColor: useMobileStyle ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.7)',
          backdropFilter: 'blur(4px)',
          left: isDesktop ? 'var(--sidebar-width, 0px)' : 0,
          zIndex: overlayZIndex,
        },
        content: useMobileStyle ? {
          // Mobile bottom sheet style
          borderRadius: '1.5rem 1.5rem 0 0',
          width: '100%',
          bottom: 0,
          left: 0,
          right: 0,
          top: 'auto',
          transform: 'none',
          padding: 0,
          position: 'fixed',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          maxHeight: '90vh',
          overflowY: 'auto',
          background: 'transparent',
          border: 'none',
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif',
        } : {
          // Desktop modal style
          borderRadius: '1rem',
          maxWidth: `min(${width}, calc(100vw - var(--sidebar-width, 0px) - 2rem))`,
          width: '100%',
          top: '50%',
          left: '50%',
          right: 'auto',
          bottom: 'auto',
          transform: 'translate(-50%, -50%)',
          padding: 0,
          position: 'absolute',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          maxHeight: maxHeight,
          overflowY: 'auto',
          background: 'transparent',
          border: 'none',
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif',
        }
      }}
    >
      {/* Glass morphism container */}
      <div className={`app-dark-input-scope backdrop-blur-xl bg-gradient-to-br from-slate-800/95 via-slate-900/95 to-indigo-900/95 border border-white/20 w-full h-full flex flex-col ${
        useMobileStyle ? 'rounded-t-2xl shadow-2xl' : 'rounded-2xl shadow-2xl'
      }`}>
        {closable && (
          <button
            onClick={toggle}
            className={`text-white/70 hover:text-white transition-all duration-200 p-1.5 rounded-full hover:bg-white/20 backdrop-blur-sm border border-white/10 hover:border-white/30 hover:scale-110 z-10 ${
              useMobileStyle ? 'top-3 right-3' : 'absolute top-4 right-4'
            }`}
            aria-label="Cerrar"
          >
            <CloseIcon />
          </button>
        )}
        {/* Title area */}
        <div className={`w-full flex flex-col items-start pb-4 ${
          useMobileStyle ? 'px-6 pt-4' : 'px-8 pt-8'
        }`}>
          {showLogo && !titleSlot && <img src="/esporai-logo.png" alt="Espor.ai" className="h-10 w-auto mb-3 mx-auto" />}
          {titleSlot ? (
            <div className="flex items-center gap-2 mb-1 w-full">{titleSlot}</div>
          ) : title ? (
            <h2 className="text-lg font-bold text-white w-full leading-tight">{title}</h2>
          ) : null}
          {description && <p className="text-white/70 text-sm w-full mt-1">{description}</p>}
        </div>
        <div className="w-full border-t border-white/20" />
        {/* Content */}
        <div
          className="w-full flex-1"
          style={{
            padding: useMobileStyle ? '1rem 1.5rem 1.5rem' : contentPadding,
            paddingTop: useMobileStyle ? '1rem' : '1.5rem'
          }}
        >
          {children}
        </div>
        {hasFooter && <div className="w-full border-t border-white/20 mt-4" />}
        {/* Footer */}
        {hasFooter && (
          <div className={`flex gap-2 pt-4 w-full pb-6 my-auto justify-start ${
            useMobileStyle ? 'px-6' : 'px-8'
          }`}>
            {showSubmitButton && (
              <button
                className={
                  destructive
                    ? "bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-5 py-2.5 rounded-lg text-sm font-semibold shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 active:scale-[0.98]"
                    : "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 active:scale-[0.98]"
                }
                onClick={onSubmit}
                disabled={isSubmitting || submitDisabled}
              >
                {submitButtonText}
              </button>
            )}
            {showCloseButton && (
              <button
                className="bg-white/10 hover:bg-white/20 active:bg-white/30 text-white/90 px-5 py-2.5 rounded-lg text-sm font-semibold backdrop-blur-md border border-white/30 hover:border-white/40 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 active:scale-[0.98]"
                onClick={onClose}
                disabled={isSubmitting}
              >
                {closeButtonText}
              </button>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default GenericModal;
